/**
 * MessageBubble — WhatsApp / Telegram-style chat bubble (issue #351,
 * subtask 09 refresh).
 *
 * Outgoing bubbles (the viewer's own messages) render right-aligned,
 * light-green, with no avatar and no name header. Incoming bubbles
 * render left-aligned, white, with a round initial-based avatar to
 * the left of the bubble; the author's name is rendered INSIDE the
 * bubble as a header line above the body (Telegram convention — see
 * Image #11 in the subtask brief).
 *
 * ``viewerKind`` lets the same component double for both surfaces:
 * the user widget passes ``'user'`` (so admin/agent replies render
 * as incoming) and the admin tool passes ``'admin'`` (so the admin's
 * own replies render as outgoing while user / agent messages render
 * as incoming).
 *
 * Name resolution priority for incoming bubbles:
 *   1. ``authorLabel`` prop (caller override — typical for the admin
 *      tool which looks up the platform user's display name)
 *   2. ``message.author_display_name`` from the backend
 *      (denormalised from the User table)
 *   3. Per-kind fallback: "Support-Bot" for agent (legally mandated),
 *      "Hofsync Support" for admin
 *
 * For incoming admin bubbles, the resolved real name is suffixed
 * with " (Hofsync Support)" so the recipient knows which support
 * person they're talking to AND that the message is from the
 * support team.
 *
 * Soft-deleted rows render as a tombstone. The original body is
 * preserved on the row for the audit chain — the frontend just hides
 * it.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { Avatar } from './Avatar';
import { MessageSources } from './MessageSources';
import { ChatMarkdownBody } from './chatMarkdown';
import type { ChatMessage } from '../../services/chatApi';

export type ViewerKind = 'user' | 'admin';

export interface MessageBubbleProps {
  message: ChatMessage;
  /** Identity of the calling surface. Determines which bubbles render
   * right-aligned ("outgoing"). Default ``'user'``. */
  viewerKind?: ViewerKind;
  /** Optional explicit author label override (highest priority). */
  authorLabel?: string;
  /** Invoked when a cited-document badge (#1757) is clicked, with the
   * document_id. Wired by the chat container (KiAssistantTab) to react-router
   * navigation so the main app deep-links to the document while the chat
   * overlay stays mounted. Absent on the shared admin tool, whose agent
   * messages never carry ``sources`` — so the bubble stays router-agnostic. */
  onOpenDocument?: (documentId: string) => void;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

export const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  viewerKind = 'user',
  authorLabel,
  onOpenDocument,
}) => {
  const { t } = useTranslation();
  const isAgent = message.author_kind === 'agent';
  const isAdmin = message.author_kind === 'admin';
  const isOutgoing = message.author_kind === viewerKind;
  const isDeleted = Boolean(message.deleted_at);
  const time = formatTime(message.sent_at);

  // Resolve the visible author label per the priority chain above.
  // For incoming admin bubbles, append " (Hofsync Support)" so the
  // recipient sees both the human's name and the team identifier.
  const supportSuffix = ` (${t('chat.author.support', 'Hofsync Support')})`;
  const resolvedAuthor = (() => {
    if (authorLabel) {
      return isAdmin ? `${authorLabel}${supportSuffix}` : authorLabel;
    }
    const fromMessage = message.author_display_name?.trim() ?? '';
    if (fromMessage) {
      return isAdmin ? `${fromMessage}${supportSuffix}` : fromMessage;
    }
    if (isAgent) return t('chat.author.supportBot', 'Support-Bot');
    if (isAdmin) return t('chat.author.support', 'Hofsync Support');
    return '';
  })();

  const showAvatar = !isOutgoing && !isDeleted;
  const showAuthorHeader = !isOutgoing && !isDeleted && Boolean(resolvedAuthor);

  return (
    <div
      data-testid="chat-bubble"
      data-author-kind={message.author_kind}
      data-message-id={message.message_id}
      data-deleted={isDeleted ? 'true' : undefined}
      className={`chat-bubble-row chat-bubble-row--${message.author_kind}${isOutgoing ? ' chat-bubble-row--outgoing' : ' chat-bubble-row--incoming'}`}
    >
      <div className="chat-bubble-row__inner">
        {showAvatar && (
          <div className="chat-bubble-row__avatar">
            <Avatar name={resolvedAuthor || '?'} size={32} bot={isAgent} />
          </div>
        )}
        <div className="chat-bubble-row__content">
          <div
            className={`chat-bubble chat-bubble--${message.author_kind}${isOutgoing ? ' chat-bubble--outgoing' : ' chat-bubble--incoming'}${isDeleted ? ' chat-bubble--deleted' : ''}`}
          >
            {showAuthorHeader && (
              <div className="chat-bubble__author">{resolvedAuthor}</div>
            )}
            {isDeleted ? (
              <em>{t('chat.bubble.deletedTombstone', 'This message was deleted')}</em>
            ) : isAgent ? (
              // Agent replies are emitted as Markdown (## headings, **bold**,
              // - bullets). Rendered through the shared sanitised pipeline
              // (chatMarkdown) — rehype-sanitize guards against HTML injection,
              // NEVER dangerouslySetInnerHTML. User/admin bubbles stay plain
              // text (the deleted-tombstone branch above is also plain).
              <ChatMarkdownBody body={message.body ?? ''} />
            ) : (
              <span className="chat-bubble__body">{message.body}</span>
            )}
            {time && <span className="chat-bubble__time-inline">{time}</span>}
            {/* Admin-side read receipt: WhatsApp-style green tick on
              * outgoing admin bubbles once the user has marked the
              * message read. Hidden on user / agent views and on
              * unread admin bubbles. */}
            {isOutgoing && isAdmin && message.read_by_user_at && !isDeleted && (
              <span
                className="chat-bubble__read-tick"
                title={t('chat.bubble.readByUser', 'Read by user')}
                aria-label={t('chat.bubble.readByUser', 'Read by user')}
              >
                <Check size={14} aria-hidden />
              </span>
            )}
            {/* Cited-document badges (#1757): a STRUCTURED region built from
              * ``message.sources`` (subtask 06), rendered BELOW the sanitized
              * markdown — never inside it, so ``rehype-sanitize`` is untouched.
              * Only agent bubbles carry sources; clicks route through the
              * ``onOpenDocument`` callback wired at the chat container. */}
            {isAgent && !isDeleted && message.sources && message.sources.length > 0 && (
              <MessageSources sources={message.sources} onOpenDocument={onOpenDocument} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
